import React,{useRef,useState}from'react';
import*as FiIcons from'react-icons/fi';
import SafeIcon from'../common/SafeIcon';
import{getQuestionnaireTemplates}from'../utils/questionnaireTemplateStorage';
import{parseWooCommerceInvoice}from'../utils/invoiceParser';
import{serviceOptions}from'../data/serviceOptions';
import'./invoice-import.css';

const{
  FiAlertTriangle,
  FiFileText,
  FiUploadCloud,
  FiX
}=FiIcons;

const eventTypes=[
  'Wedding',
  'Corporate Event',
  'Bar Mitzvah',
  'Bat Mitzvah',
  'B’nai Mitzvah',
  'Birthday',
  'Other'
];

const emptyForm={
  name:'',
  client:'',
  email:'',
  phone:'',
  type:'Wedding',
  date:'',
  venue:'',
  guests:'',
  dj:'',
  eventDuration:'',
  eventStartTime:'',
  setupStartTime:'',
  eventEndTime:'',
  services:[],
  serviceDetails:''
};

const importedFields=[
  'name',
  'client',
  'email',
  'type',
  'date',
  'venue'
];

function getTemplateCategory(eventType){
  const normalizedType=String(eventType||'').toLowerCase();

  if(normalizedType.includes('mitzvah')){
    return{label:'Bar/Bat Mitzvah',names:['bar bat mitzvah']};
  }
  if(normalizedType.includes('wedding')){
    return{label:'Wedding',names:['wedding']};
  }
  if(normalizedType.includes('corporate')){
    return{label:'Corporate',names:['corporate','corporate event']};
  }
  if(normalizedType.includes('birthday')){
    return{label:'Birthday',names:['birthday']};
  }
  return null;
}

function getTemplateForEventType(templates,eventType){
  const category=getTemplateCategory(eventType);
  if(!category)return null;

  return templates.find((template)=>{
    const normalizedName=String(template.name||'')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g,' ')
      .trim();
    return category.names.includes(normalizedName);
  })||null;
}

function CreateEventModal({onClose,onCreate}){
  const fileRef=useRef(null);
  const[form,setForm]=useState(emptyForm);
  const[questionnaireTemplates,setQuestionnaireTemplates]=useState(
    ()=>getQuestionnaireTemplates()
  );
  const[selectedQuestionnaire,setSelectedQuestionnaire]=useState('wedding');
  const[importState,setImportState]=useState('');
  const[error,setError]=useState('');

  const update=(key,value)=>{
    setError('');
    setForm((current)=>({...current,[key]:value}));
  };

  const toggleService=(service)=>{
    setForm((current)=>({
      ...current,
      services:current.services.includes(service)
        ?current.services.filter((item)=>item!==service)
        :[...current.services,service]
    }));
  };

  const chooseQuestionnaire=(templateId)=>{
    const latestTemplates=getQuestionnaireTemplates();
    const template=latestTemplates.find((item)=>item.id===templateId);

    setQuestionnaireTemplates(latestTemplates);
    setSelectedQuestionnaire(template?.id||'');
    setError('');

    if(template?.eventType){
      setForm((current)=>({
        ...current,
        type:template.eventType
      }));
    }
  };

  const importInvoice=async(event)=>{
    const file=event.target.files?.[0];

    if(!file){
      return;
    }

    setError('');
    setImportState('Reading invoice...');

    try{
      const imported=await parseWooCommerceInvoice(file);
      const validImported=Object.fromEntries(
        importedFields
          .filter((field)=>imported[field])
          .map((field)=>[field,imported[field]])
      );

      if(Object.keys(validImported).length===0){
        throw new Error(
          'No event details were found. Use a text-based PDF,WooCommerce CSV,JSON export,or text invoice.'
        );
      }

      const latestTemplates=getQuestionnaireTemplates();
      const importedType=validImported.type||'Wedding';
      const matchingTemplate=getTemplateForEventType(
        latestTemplates,
        importedType
      );
      const syncedType=importedType;

      setForm((current)=>({
        ...current,
        ...validImported,
        type:syncedType
      }));
      setSelectedQuestionnaire(matchingTemplate?.id||'');
      setQuestionnaireTemplates(latestTemplates);

      const labels={
        name:'event name',
        client:'client name',
        email:'email',
        type:'event type',
        date:'event date',
        venue:'venue'
      };

      const importedLabels=Object.keys(validImported)
        .map((field)=>labels[field])
        .join(',');

      setImportState(
        `Imported ${importedLabels} from ${file.name}. Review the fields before creating the event.`
      );
    }catch(importError){
      setImportState('');
      setError(importError.message||'The invoice could not be read.');
    }finally{
      event.target.value='';
    }
  };

  const submit=(event)=>{
    event.preventDefault();

    if(!form.name.trim()||!form.client.trim()){
      setError(
        'Add an event name and client name before creating the event.'
      );
      return;
    }

    const latestTemplates=getQuestionnaireTemplates();
    const questionnaireTemplate=latestTemplates.find(
      (template)=>template.id===selectedQuestionnaire
    );

    if(!questionnaireTemplate){
      const category=getTemplateCategory(form.type);
      if(category&&!getTemplateForEventType(latestTemplates,form.type)){
        setError(`The ${category.label} questionnaire template does not exist. Choose another template or add it in Questionnaires.`);
      }else{
        setError('Select a questionnaire template before creating the event.');
      }
      return;
    }

    const editQuestionnaire=
      event.nativeEvent.submitter?.value==='questionnaire';
    const sharedDetails=form.serviceDetails.trim();

    onCreate(
      {
        ...form,
        type:form.type,
        name:form.name.trim(),
        client:form.client.trim(),
        email:form.email.trim(),
        phone:form.phone.trim(),
        venue:form.venue.trim(),
        eventDuration:form.eventDuration.trim(),
        eventStartTime:form.eventStartTime,
        setupStartTime:form.setupStartTime,
        eventEndTime:form.eventEndTime,
        services:[...form.services],
        serviceDetails:sharedDetails,
        eventDescription:sharedDetails
      },
      editQuestionnaire,
      questionnaireTemplate
    );
  };

  const expectedCategory=getTemplateCategory(form.type);

  return(
    <div className="modal-backdrop">
      <form className="modal-card" onSubmit={submit}>
        <div className="modal-heading">
          <div>
            <span className="eyebrow">New booking</span>
            <h2>Create an event</h2>
          </div>

          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <SafeIcon icon={FiX}/>
          </button>
        </div>

        <label className="template-picker">
          <span>Questionnaire template</span>
          <select
            value={selectedQuestionnaire}
            onChange={(event)=>
              chooseQuestionnaire(event.target.value)
            }
            required
          >
            {questionnaireTemplates.map((template)=>(
              <option value={template.id} key={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <small>
            {expectedCategory&&!getTemplateForEventType(questionnaireTemplates,form.type)
              ?`The ${expectedCategory.label} template is missing. Choose another template or add it in Questionnaires.`
              :'The event type is automatically matched to a template when available.'}
          </small>
        </label>

        <button
          type="button"
          className="invoice-drop"
          onClick={()=>fileRef.current?.click()}
        >
          <SafeIcon icon={FiUploadCloud}/>
          <span>
            <strong>Import WooCommerce invoice</strong>
            <small>Upload PDF,CSV,or exported order data</small>
          </span>
        </button>

        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={importInvoice}
          accept=".pdf,.csv,.json,.txt"
        />

        {importState&&(
          <p className="invoice-import-status" role="status">
            <SafeIcon icon={FiFileText}/>
            {importState}
          </p>
        )}

        {error&&(
          <p className="invoice-import-error" role="alert">
            <SafeIcon icon={FiAlertTriangle}/>
            {error}
          </p>
        )}

        <div className="divider">
          <span>or enter details</span>
        </div>

        <div className="form-grid">
          <label>
            Event name
            <input
              required
              value={form.name}
              onChange={(event)=>update('name',event.target.value)}
            />
          </label>

          <label>
            Client name
            <input
              required
              value={form.client}
              onChange={(event)=>update('client',event.target.value)}
            />
          </label>

          <label>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(event)=>update('email',event.target.value)}
            />
          </label>

          <label>
            Client phone number
            <input
              type="tel"
              value={form.phone}
              onChange={(event)=>update('phone',event.target.value)}
              placeholder="949-394-2572"
            />
          </label>

          <label>
            Event type
            <select
              value={form.type}
              onChange={(event)=>{
                const eventType=event.target.value;
                const matchingTemplate=getTemplateForEventType(
                  questionnaireTemplates,
                  eventType
                );
                setError('');
                setForm((current)=>({...current,type:eventType}));
                setSelectedQuestionnaire(matchingTemplate?.id||'');
              }}
            >
              {eventTypes.map((type)=>(
                <option key={type}>{type}</option>
              ))}
            </select>
            <small>
              Choose an event type to select its matching questionnaire.
            </small>
          </label>

          <label>
            Event date
            <input
              type="date"
              value={form.date}
              onChange={(event)=>update('date',event.target.value)}
            />
          </label>

          <label>
            Venue
            <input
              value={form.venue}
              onChange={(event)=>update('venue',event.target.value)}
            />
          </label>

          <label>
            Guest count
            <input
              type="number"
              min="0"
              value={form.guests}
              onChange={(event)=>update('guests',event.target.value)}
            />
          </label>

          <label>
            Event Duration
            <input
              value={form.eventDuration}
              onChange={(event)=>
                update('eventDuration',event.target.value)
              }
              placeholder="Example: 6 hours"
            />
          </label>

          <label>
            Event Start Time
            <input
              type="time"
              value={form.eventStartTime}
              onChange={(event)=>
                update('eventStartTime',event.target.value)
              }
            />
          </label>

          <label>
            Setup Start Time
            <input
              type="time"
              value={form.setupStartTime}
              onChange={(event)=>
                update('setupStartTime',event.target.value)
              }
            />
          </label>

          <label>
            Event End Time
            <input
              type="time"
              value={form.eventEndTime}
              onChange={(event)=>
                update('eventEndTime',event.target.value)
              }
            />
          </label>

          <fieldset className="choice-field form-field full services-picker">
            <legend>Services booked</legend>
            <p className="services-picker-help">
              Select every service included in this event.
            </p>

            <div className="services-picker-options">
              {serviceOptions.map((service)=>{
                const selected=form.services.includes(service);

                return(
                  <button
                    type="button"
                    key={service}
                    className={selected?'selected':''}
                    onClick={()=>toggleService(service)}
                    aria-pressed={selected}
                  >
                    <span className="service-picker-check">
                      {selected?'✓':''}
                    </span>
                    <span>{service}</span>
                  </button>
                );
              })}
            </div>

            <span className="services-picker-count">
              {form.services.length===0
                ?'No services selected'
                :`${form.services.length} ${form.services.length===1?'service':'services'} selected`}
            </span>
          </fieldset>

          <label className="form-field full">
            <span>Event Description,Details and Specifications</span>
            <textarea
              value={form.serviceDetails}
              onChange={(event)=>
                update('serviceDetails',event.target.value)
              }
              placeholder="Describe the event and add details or specifications for the services selected..."
              rows="5"
            />
          </label>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="button secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            name="destination"
            value="questionnaire"
            className="button secondary"
          >
            <SafeIcon icon={FiFileText}/>
            Create & edit questionnaire
          </button>

          <button
            name="destination"
            value="event"
            className="button primary"
          >
            <SafeIcon icon={FiFileText}/>
            Create event
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateEventModal;